import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { differenceInMonths, differenceInYears } from 'date-fns';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { confidentialAPI } from '../../services/api';
import { useStaffList } from '../../hooks/useStaffData';
import { useDepartments } from '../../hooks/useConfigData';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ArrowLeft,
  User,
  Building,
  Calendar,
  AlertTriangle,
  CheckCircle,
  UserMinus,
  Users,
  FileText,
} from 'lucide-react';
import { FilterButton } from '../../components/UI/FilterButton';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import FormSelect from '../../components/UI/FormSelect';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Pagination } from '../../components/UI/Pagination';
import { DepartmentTradeChips } from '../../components/UI/DepartmentTradeChips';
import { getClassificationColor } from '../../utils/classificationConfig';
import { formatDate } from '../../utils/dateUtils';

const EXIT_TYPE_OPTIONS = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'mutual_agreement', label: 'Mutual separation' },
  { value: 'contract_end', label: 'Contract expiry' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'termination_with_notice', label: 'Termination with notice' },
  { value: 'termination_without_notice', label: 'Immediate termination' },
  { value: 'summary_dismissal', label: 'Summary dismissal' },
  { value: 'retrenchment', label: 'Retrenchment' },
  { value: 'death', label: 'Death in service' },
];

const DEFAULT_FILTERS = {
  department: '',
  status: 'Active',
};

const PAGE_SIZE = 100;
const MIN_SEARCH_CHARS = 2;
const SYSTEM_STAFF_IDS = new Set(['A0001', 'S0000']);

const EMPLOYEE_ROW_GRID =
  'lg:grid lg:grid-cols-[2.5rem_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.1fr)_6.75rem] lg:gap-4 lg:items-center';

const getDepartmentDisplayName = (department) => {
  if (!department) return '';
  if (typeof department === 'string') return department;
  if (typeof department === 'object') return department.department_name || department.name || '';
  return '';
};

const getLegalName = (employee) => {
  if (!employee) return '';
  return `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
};

const getEmployeeName = (employee) => {
  if (!employee) return '';
  const alias = employee.alias?.trim();
  const legal = getLegalName(employee);
  if (alias && legal && alias.toLowerCase() !== legal.toLowerCase()) {
    return `${alias} (${legal})`;
  }
  return alias || legal || employee.full_name || employee.staff_id || '';
};

const getDesignation = (employee) => {
  if (!employee) return '';
  return (employee.designation || employee.mom_designation || '').trim();
};

const getEmploymentStatusKey = (status) => {
  const key = (status || '').toLowerCase();
  if (key === 'active') return 'active';
  if (key === 'inactive') return 'inactive';
  return key || 'active';
};

const isLocalEmployee = (employee) => {
  const passType = employee?.employment_pass_type;
  return passType === 'Singapore Citizen' || passType === 'Permanent Resident';
};

const getServiceTenure = (dateJoined) => {
  if (!dateJoined) return 'Tenure unavailable';
  const start = new Date(dateJoined);
  if (Number.isNaN(start.getTime())) return 'Tenure unavailable';

  const now = new Date();
  const years = differenceInYears(now, start);
  if (years >= 1) return `${years} year${years === 1 ? '' : 's'} of service`;

  const months = differenceInMonths(now, start);
  if (months >= 1) return `${months} month${months === 1 ? '' : 's'} of service`;
  return 'Less than 1 month of service';
};

const getVisibleSteps = (employee) => {
  const local = isLocalEmployee(employee);
  const steps = [
    { id: 1, name: 'Resignation Details' },
    { id: 2, name: 'HOD Discussion & Leave Settlement' },
    { id: 3, name: 'HR Exit Interview' },
    { id: 4, name: 'Sponsorship Bond Check' },
    { id: 5, name: 'Final Settlement Calculation' },
    { id: 6, name: 'IR21 Processing', skipForLocal: true },
    { id: 7, name: 'Final Offboarding' },
  ];
  return steps.filter((step) => !(local && step.skipForLocal));
};

const NewOffboarding = () => {
  const navigate = useNavigate();
  const { canAccessAdmin } = useAuth();
  const isCompact = useIsMobile(1024);
  const hasAdminAccess = canAccessAdmin();

  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [exitType, setExitType] = useState('resignation');

  const { data: departments = [] } = useDepartments();
  const departmentOptions = useMemo(() => (
    departments
      .map((dept) => dept.department_name || dept.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
  ), [departments]);

  const trimmedSearch = searchTerm.trim();
  const hasSearch = trimmedSearch.length >= MIN_SEARCH_CHARS;
  const hasDepartment = Boolean(filters.department);

  const staffParams = useMemo(() => {
    const params = { page, page_size: PAGE_SIZE, ordering: 'first_name,last_name' };
    if (hasSearch) params.search = trimmedSearch;
    if (hasDepartment) params.department = filters.department;
    if (filters.status === 'Active') params.employment_status = 'active';
    if (filters.status === 'Inactive') params.employment_status = 'inactive';
    return params;
  }, [page, hasSearch, trimmedSearch, hasDepartment, filters.department, filters.status]);

  const {
    data: staffData,
    isLoading,
    isError,
    refetch,
  } = useStaffList(staffParams, { enabled: hasAdminAccess });

  const filteredEmployees = useMemo(
    () => (staffData?.results || []).filter((employee) => !SYSTEM_STAFF_IDS.has(employee.staff_id)),
    [staffData],
  );
  const totalCount = staffData?.count || 0;
  const totalPages = staffData?.totalPages || 1;

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1);
    setHighlightedIndex(-1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    setHighlightedIndex((prev) => (
      filteredEmployees.length === 0 ? -1 : Math.min(prev, filteredEmployees.length - 1)
    ));
  }, [filteredEmployees]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const staffId = filteredEmployees[highlightedIndex]?.staff_id;
    if (!staffId) return;
    document.getElementById(`offboarding-employee-${staffId}`)?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, filteredEmployees]);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setHighlightedIndex(-1);
  };

  const handleClearSelection = () => {
    setSelectedEmployee(null);
    setHighlightedIndex(-1);
  };

  const createOffboardingWorkflow = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    if (creating) return;

    try {
      setCreating(true);
      const payload = {
        staff_id: selectedEmployee.staff_id,
        exit_type: exitType,
      };

      const response = await confidentialAPI.createOffboardingWorkflow(payload);
      const data = response?.data || {};
      if (response.status === 200 && (data.success || data.workflow)) {
        toast.success(`Offboarding case created for ${getEmployeeName(selectedEmployee)}`);
        navigate(`/admin/employee-lifecycle/offboarding/${data.workflow_id || data.id}`);
      } else {
        throw new Error(data.error || 'Failed to create workflow');
      }
    } catch (error) {
      const status = error?.response?.status;
      const errData = error?.response?.data || {};
      const errMsg = (typeof errData?.error === 'string' && errData.error) || error?.message || '';
      const alreadyExists = status === 400 && (
        errData.code === 'already_exists'
        || /already exists/i.test(errMsg)
        || /already exists/i.test(JSON.stringify(errData.error || ''))
      );

      if (alreadyExists) {
        if (errData?.workflow_id) {
          toast.info('An active offboarding case already exists. Opening it.');
          navigate(`/admin/employee-lifecycle/offboarding/${errData.workflow_id}`);
          return;
        }
        toast.warning('No open case was found. Try Start Offboarding again.');
        return;
      } else if (status === 404 && /Employee not found/i.test(errMsg)) {
        toast.error('Employee not found. Please refresh and try again.');
      } else if (status === 401) {
        toast.error('Unauthorized to create offboarding workflow. Please re-login.');
      } else {
        toast.error(errMsg || 'Failed to create offboarding workflow');
      }
    } finally {
      setCreating(false);
    }
  };

  const activeFilterCount = (filters.department ? 1 : 0)
    + (filters.status !== 'Active' ? 1 : 0);

  const showList = !isCompact || !selectedEmployee;
  const showDetail = !isCompact || !!selectedEmployee;
  const visibleSteps = getVisibleSteps(selectedEmployee);

  if (!hasAdminAccess) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-content text-center py-12">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h1>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              You need administrator permissions to start an offboarding case.
            </p>
            <Link to="/admin/employee-lifecycle" className="btn btn-admin-outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Lifecycle
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/admin/employee-lifecycle"
          aria-label="Back to Employee Lifecycle"
          title="Back to Employee Lifecycle"
          className="icon-button-admin flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">Start New Offboarding</h1>
          <p className="text-sm text-gray-500 mt-1">
            Confirm the employee, then start their exit case
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_26rem] gap-6 items-start">
        {showList && (
          <div className="card min-w-0">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">Select Employee</h2>
                    {!isLoading && (
                      <CountBadge
                        count={totalCount}
                        theme="admin"
                        size="md"
                        variant="solid"
                        max={9999}
                        className="flex-shrink-0"
                      />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    Active staff, A–Z. Search or filter if the name is further down.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <DebouncedSearchInput
                  value={searchTerm}
                  onSearch={handleSearch}
                  placeholder="Search name, staff ID, or email..."
                  theme="admin"
                  className="w-full sm:max-w-xl"
                />
                <FilterButton
                  theme="admin"
                  isOpen={showFilters}
                  activeCount={activeFilterCount}
                  onClick={() => setShowFilters((v) => !v)}
                />
              </div>

              {showFilters && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label" htmlFor="offboarding-filter-department">Department</label>
                    <select
                      id="offboarding-filter-department"
                      className="select-admin w-full"
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                    >
                      <option value="">All departments</option>
                      {departmentOptions.map((dep) => (
                        <option key={dep} value={dep}>{dep}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label" htmlFor="offboarding-filter-status">Status</label>
                    <select
                      id="offboarding-filter-status"
                      className="select-admin w-full"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="All">All</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div>
              {isError ? (
                <div className="text-center py-12 px-4">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load employees</h3>
                  <p className="text-sm text-gray-500 mb-4">Refresh and try again, or contact support if this continues.</p>
                  <button type="button" onClick={() => refetch()} className="btn btn-admin-outline">
                    Retry
                  </button>
                </div>
              ) : isLoading ? (
                <div className="p-4 sm:p-6 space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="p-4 border border-gray-200 rounded-xl animate-pulse">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-1/3" />
                          <div className="h-3 bg-gray-200 rounded w-1/4" />
                        </div>
                        <div className="h-6 bg-gray-200 rounded-full w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No employees found</h3>
                  <p className="text-sm text-gray-500">
                    Try a different name, staff ID, or clear the filters.
                  </p>
                </div>
              ) : (
                <>
                <div
                  className="p-4 sm:p-6 space-y-3"
                  tabIndex={0}
                  aria-label="Employees available for offboarding"
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setHighlightedIndex((i) => Math.min((i < 0 ? 0 : i + 1), filteredEmployees.length - 1));
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightedIndex((i) => Math.max(i < 0 ? 0 : i - 1, 0));
                    }
                    if (e.key === 'Enter' && filteredEmployees[highlightedIndex]) {
                      handleEmployeeClick(filteredEmployees[highlightedIndex]);
                    }
                  }}
                >
                  <div className={`hidden ${EMPLOYEE_ROW_GRID} px-4 py-0 border border-transparent text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    <span />
                    <span>Employee</span>
                    <span>Department</span>
                    <span>Joined</span>
                    <span>Pass type</span>
                    <span className="text-right">Status</span>
                  </div>

                  {filteredEmployees.map((employee, index) => {
                    const isSelected = selectedEmployee?.staff_id === employee.staff_id;
                    const isHighlighted = highlightedIndex === index;
                    const departmentName = getDepartmentDisplayName(employee.department);

                    return (
                      <button
                        key={employee.staff_id}
                        id={`offboarding-employee-${employee.staff_id}`}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => handleEmployeeClick(employee)}
                        className={`w-full text-left p-4 rounded-xl border box-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                          isSelected
                            ? 'bg-red-50 border-red-500'
                            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        } ${isHighlighted && !isSelected ? 'bg-red-50/60 border-red-300' : ''}`}
                      >
                        <div className={`flex items-start gap-3 ${EMPLOYEE_ROW_GRID}`}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getClassificationColor(employee.employment_classification)}`}>
                            <User className="w-5 h-5" />
                          </div>

                          <div className="min-w-0 flex-1 lg:flex-none">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {getEmployeeName(employee)}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {employee.staff_id}
                              {getDesignation(employee) ? ` · ${getDesignation(employee)}` : ''}
                            </div>
                            <div className="lg:hidden mt-1.5 space-y-1 text-xs text-gray-500">
                              <DepartmentTradeChips
                                department={departmentName}
                                trade={employee.trade_category}
                                layout="inline"
                              />
                              <div>
                                Joined {formatDate(employee.date_joined)}
                                {employee.employment_pass_type ? ` · ${employee.employment_pass_type}` : ''}
                              </div>
                            </div>
                          </div>

                          <div className="hidden lg:block min-w-0">
                            <DepartmentTradeChips
                              department={departmentName}
                              trade={employee.trade_category}
                            />
                          </div>

                          <div className="hidden lg:block min-w-0">
                            <p className="text-sm text-gray-900 truncate">{formatDate(employee.date_joined)}</p>
                          </div>

                          <div className="hidden lg:block min-w-0">
                            <p className="text-sm text-gray-900 truncate">
                              {employee.employment_pass_type || '—'}
                            </p>
                          </div>

                          <div className="flex items-center justify-end gap-2 w-full">
                            <StatusBadge status={getEmploymentStatusKey(employee.employment_status)} />
                            <CheckCircle
                              className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-red-600' : 'invisible'}`}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalCount={totalCount}
                  pageSize={PAGE_SIZE}
                  theme="admin"
                  noun="employees"
                />
                </>
              )}
            </div>
          </div>
        )}

        {showDetail && (
          <div className="space-y-6 lg:sticky lg:top-6">
            <div className="card">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 truncate">Confirm details</h2>
                  </div>
                  {isCompact && selectedEmployee && (
                    <button
                      type="button"
                      onClick={handleClearSelection}
                      className="btn btn-admin-outline btn-sm flex-shrink-0"
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>

              {selectedEmployee ? (
                <div className="p-4 sm:p-6">
                  <div className="flex items-start mb-5">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${getClassificationColor(selectedEmployee.employment_classification)}`}>
                      <User className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {getEmployeeName(selectedEmployee)}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{selectedEmployee.staff_id}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Building className="w-4 h-4 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {getDepartmentDisplayName(selectedEmployee.department) || '—'}
                        </p>
                        {getDesignation(selectedEmployee) && (
                          <p className="text-xs text-gray-500 truncate">{getDesignation(selectedEmployee)}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Calendar className="w-4 h-4 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          Joined {formatDate(selectedEmployee.date_joined)}
                        </p>
                        <p className="text-xs text-gray-500">{getServiceTenure(selectedEmployee.date_joined)}</p>
                      </div>
                    </div>

                    {selectedEmployee.employment_pass_type && (
                      <div className="flex items-start">
                        {isLocalEmployee(selectedEmployee) ? (
                          <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{selectedEmployee.employment_pass_type}</p>
                          <p className={`text-xs mt-1 ${isLocalEmployee(selectedEmployee) ? 'text-green-700' : 'text-orange-700'}`}>
                            {isLocalEmployee(selectedEmployee)
                              ? 'No IR21 processing required'
                              : 'IR21 processing will be required'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200 space-y-4">
                    <div>
                      <label className="form-label">Exit type</label>
                      <FormSelect
                        theme="admin"
                        value={exitType}
                        onChange={setExitType}
                        options={EXIT_TYPE_OPTIONS}
                        placeholder="Select exit type"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      You are about to start offboarding for{' '}
                      <span className="font-medium text-gray-700">
                        {getEmployeeName(selectedEmployee)} · {selectedEmployee.staff_id}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={createOffboardingWorkflow}
                      disabled={creating || !exitType}
                      className="btn btn-admin-primary w-full"
                    >
                      {creating ? (
                        <>
                          <LoadingSpinner size="sm" color="white" className="mr-2" />
                          <span>Creating case...</span>
                        </>
                      ) : (
                        <>
                          <UserMinus className="w-4 h-4 mr-2" />
                          <span>Start Offboarding</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 px-6">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No employee selected</h3>
                  <p className="text-sm text-gray-500">
                    Choose someone from the list to review their details before starting the case.
                  </p>
                </div>
              )}
            </div>

            <div className="card">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 truncate">What happens next</h2>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-3">
                  {visibleSteps.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                      <div className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-sm text-gray-700">{step.name}</span>
                    </div>
                  ))}
                </div>

                {selectedEmployee && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-600">
                      {isLocalEmployee(selectedEmployee)
                        ? `${visibleSteps.length}-step process — no IR21 required for local employees`
                        : `${visibleSteps.length}-step process — IR21 required for foreign workers`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewOffboarding;
